import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import isEmail from "validator/lib/isEmail";
import { Form, Formik } from "formik";
import LocationBarPopup from "./LocationBarPopup";
import LocationBarSlider from "./LocationBarSlider";
import { addEmailInInterestList } from "../../store/api";
import { steps } from "./staticData";
import { PARTNERS } from "../../utils/constants";
import { useOrderDetail } from "../../store/orderDetail";
import { useOrganization } from "../../store/organization";
import { redirectSkiptiCheckoutFlow, mergeSearchParams, redirectSkiptiDiscoverPage } from "../../helpers";

export const ZipEntryFlow = ({ isZipModalOpen, setIsZipModalOpen }) => {
  const params = useParams();
  const location = useLocation();
 const [searchParams] = useSearchParams();

  const {zip, client, getZipInformation, updateFields} = useOrderDetail();
  const { data: organizationData } = useOrganization(
    (state) => state.organization
  );

  const matches = useMediaQuery("(min-width:768px)");

  const [texts, setText] = useState({
    availableText: "",
    address: "",
  });

  
  const [isProcessing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(steps.zipCodeStep);
  const formRef = useRef(null);

  const openPopup = useCallback(() => {
    setIsZipModalOpen(true);
  }, [
    //isAvailabilityError, isServiced, canShowProductsInPopup
  ]);

  // useEffect(() => {
  //   if (zipModalOpen) {
  //     openPopup();
  //   }
  // }, [zipModalOpen]);

  useEffect(() => {
    window.analytics.track("Register Interest - New Zip Entry Pop-up Opened", {
      url: window.location.href,
      referrer: document.referrer,
      searchParams: searchParams,
      params: params,
      // category: params.category,
      // group: params.group,
      client: client,
      zip: zip,
    });
    // return () => {
    //   second;
    // };
  }, []);

  const closePopup = (showMessage = true) => {
    window.analytics.track("Register Interest - New Zip Entry Pop-up Closed", {
      url: window.location.href,
      referrer: document.referrer,
      searchParams: searchParams,
      params: params,
      // category: params.category,
      // group: params.group,
      client: client,
      zip: zip,
    });
    // return () => {
    //   second;
    // };
    setCurrentStep(steps.zipCodeStep);
    setIsZipModalOpen(false);
  };

  async function onAddEmail(email) {
    return await addEmailInInterestList({
      email: email,
      zipPostal: String(zip),
      // ipAddress: null,
      organizationId: organizationData.organizationId,
      productId: "freetrialride", // @todo: check this
    });
  }

  function redirectMainFlow(zipInformation) {
    const pathnameArray = location.pathname.split("/");

    const clientString = !client ? "" : `client=${PARTNERS.JACK_RABBIT}`;
    const zipString = !zipInformation.zipPostal ? "" : `zip=${zipInformation.zipPostal}`;

    let params = mergeSearchParams(location.search, [
      zipString,
      clientString,
    ]);

    let productId = zipInformation?.skiptiProductId;
    let redirectionPath = `/${pathnameArray[1]}/${pathnameArray[2]}/${productId}`;

    if (!productId) {
      if (zipInformation?.hasAlternate) {
        // Should go to the alternate product show page
        productId = zipInformation?.skiptiAlternateId;

        redirectionPath = `/items/${productId}`;
        params += "&alternative=true";
      } else {
        // Should go to the partner page
        redirectionPath = `/partners/${client}?alternative=false&`;
        params = zipString;
      }
    }

    // Redirect to the original product show page
    return redirectSkiptiCheckoutFlow(redirectionPath, params);
  }

  const submitHandler = async (values) => {
    updateFields({ zip: values.zipCode });

    switch (currentStep) {
      case steps.zipCodeStep: {
        setProcessing(true);

        const zipInformationRequestBody = {
          zip: values.zipCode,
          externalId: params.freetrialride,
          client: client,
        };

        const zipInfo = await getZipInformation(
          ...Object.values(zipInformationRequestBody)
        );

        setProcessing(false);

        if (zipInfo && zipInfo.serviced) {
          setIsZipModalOpen(false);
          formRef.current.handleReset();

          redirectMainFlow(zipInfo);
        } else {
          setCurrentStep(steps.unservicedAreaStep);
        }
        break;
      }
      case steps.unservicedAreaStep:
        if (formRef.current.values.email) {
          setProcessing(true);

          const response = await onAddEmail(values.email);

          setProcessing(false);

          if (response?.isOk) {
            setCurrentStep(steps.thankYouStep);
          }
        } else {
          closePopup();
        }
        break;

      case steps.thankYouStep: {
        setCurrentStep(steps.zipCodeStep);
        formRef.current.handleReset();
        break;
      }
      default: {
        break;
      }
    }
  };
   function isValidZipCode(zipCode) {
    return /^(?!00000)\d{5}(?:[-\s]\d{4})?$/.test(zipCode);
  }

  const validationReason = async (values) => {
    const errors = {};
    if (values.zipCode.length > 5) {
      errors.zipCode = "Zipcode length should be 5 digits";
    } else if (!isValidZipCode(values.zipCode) || values.zipCode === "00000") {
      errors.zipCode = "You added an invalid Zip Code!";
    }
    if (values.email && !isEmail(values.email)) {
      errors.email = "Invalid Email!";
    }
    return errors;
  };

  const additionalTextsHandler = () => {
    switch (currentStep) {
      case steps.zipCodeStep: {
        setCurrentStep(steps.howDoesItWork);
        break;
      }
      case steps.unservicedAreaStep: {
        formRef.current.handleReset();
        setCurrentStep(steps.zipCodeStep);
        break;
      }
      case steps.thankYouStep: {
        redirectSkiptiDiscoverPage();
        break;
      }
      case steps.howDoesItWork: {
        setCurrentStep(steps.zipCodeStep);
        break;
      }

      default:
        break;
    }
  };

  return (
    <div>
      <Formik
        initialValues={{
          zipCode: zip,
          email: "",
        }}
        enableReinitialize
        validate={validationReason}
        innerRef={formRef}
      >
        {({ handleChange, values, errors }) => (
          <Form>
            {matches ? (
              <LocationBarPopup
                texts={texts}
                toggle={closePopup}
                isZipModalOpen={isZipModalOpen}
                currentStep={currentStep}
                onSubmit={() => submitHandler(values)}
                values={values}
                changeHandler={handleChange}
                errors={errors}
                isProcessing={isProcessing}
                additionalStepClickHandler={additionalTextsHandler}
                openDialog={openPopup}
                zip={zip}
              />
            ) : (
              <LocationBarSlider
                texts={texts}
                toggle={closePopup}
                openDialog={openPopup}
                isZipModalOpen={isZipModalOpen}
                currentStep={currentStep}
                onSubmit={() => submitHandler(values)}
                values={values}
                changeHandler={handleChange}
                errors={errors}
                isProcessing={isProcessing}
                additionalStepClickHandler={additionalTextsHandler}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};
